/*
  Favorites (feature 006, User Story 5): heart buttons on gift cards and the Saved items page.
  Contract: specs/006-home-page-redesign/contracts/favorites.md · research.md R13.
  Only public product handles are stored, on this device (localStorage "favorites:v1").
  Hearts are hidden in the markup and shown here, so no-JS visitors never see a dead control.
*/
(() => {
  if (window.giftFavorites) return;
  const KEY = 'favorites:v1';
  const MAX = 50;
  // Shopify handles may contain Unicode (for example ®); reject only characters that could break a URL or markup
  const HANDLE = /^[^\s/?#<>"'\\]{1,255}$/u;
  let memory = null; // fallback when storage is unavailable (private mode, blocked storage)

  const read = () => {
    if (memory) return memory.slice();
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && HANDLE.test(h)).slice(0, MAX) : [];
    } catch (error) {
      return [];
    }
  };

  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
      memory = null;
    } catch (error) {
      memory = list.slice();
    }
  };

  const sync = (root = document) => {
    const saved = new Set(read());
    root.querySelectorAll('[data-favorite]').forEach((button) => {
      button.hidden = false;
      button.setAttribute('aria-pressed', saved.has(button.dataset.favorite) ? 'true' : 'false');
    });
  };

  const toggle = (handle) => {
    if (!HANDLE.test(handle)) return;
    const list = read();
    const index = list.indexOf(handle);
    if (index === -1) list.unshift(handle);
    else list.splice(index, 1);
    write(list.slice(0, MAX));
    sync();
    document.dispatchEvent(new CustomEvent('favorites:change', { detail: { handle, saved: index === -1 } }));
  };

  window.giftFavorites = { read, toggle };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-favorite]');
    if (button) toggle(button.dataset.favorite);
  });
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) sync();
  });
  document.addEventListener('shopify:section:load', () => sync());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => sync());
  else sync();

  if (!customElements.get('saved-items')) {
    class SavedItems extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-saved-items-grid]');
        this.empty = this.querySelector('[data-saved-items-empty]');
        this.status = this.querySelector('[data-saved-items-status]');
        this.onChange = (event) => {
          if (!event.detail.saved) this.removeCard(event.detail.handle);
        };
        document.addEventListener('favorites:change', this.onChange);
        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener('favorites:change', this.onChange);
      }

      async render() {
        const handles = read();
        this.grid.innerHTML = '';
        if (!handles.length) return this.showEmpty();
        const cards = new Array(handles.length).fill(null);
        const missing = [];
        const renamed = new Map();
        let next = 0;
        const worker = async () => {
          while (next < handles.length) {
            const index = next++;
            const handle = handles[index];
            try {
              const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
              const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=gift-product-card`);
              if (response.status === 404) {
                missing.push(handle);
                continue;
              }
              if (!response.ok) continue;
              const html = await response.text();
              const card = new DOMParser().parseFromString(html, 'text/html').querySelector('.gift-card');
              // No card: the product no longer exists or is unavailable on this storefront
              if (!card) {
                missing.push(handle);
                continue;
              }
              // A changed handle redirects to the new product URL: remember the new handle
              if (card.dataset.productHandle && card.dataset.productHandle !== handle) {
                renamed.set(handle, card.dataset.productHandle);
              }
              cards[index] = card;
            } catch (error) {
              // network error: skip this card, keep the handle for the next visit
            }
          }
        };
        await Promise.all([worker(), worker(), worker(), worker()]); // at most 4 requests in flight
        if (missing.length || renamed.size) {
          const cleaned = read()
            .filter((h) => !missing.includes(h))
            .map((h) => renamed.get(h) || h);
          write(Array.from(new Set(cleaned)));
        }
        cards.filter(Boolean).forEach((card) => this.grid.appendChild(document.importNode(card, true)));
        sync(this.grid);
        this.updateStatus();
      }

      removeCard(handle) {
        const card = this.grid.querySelector(`.gift-card[data-product-handle="${CSS.escape(handle)}"]`);
        if (card) card.remove();
        this.updateStatus();
      }

      updateStatus() {
        const count = this.grid.querySelectorAll('.gift-card').length;
        if (!count) return this.showEmpty();
        this.empty.hidden = true;
        const template = count === 1 ? this.dataset.countOne : this.dataset.countOther;
        if (this.status) this.status.textContent = (template || '').replace('%n', String(count));
      }

      showEmpty() {
        this.empty.hidden = false;
        if (this.status) this.status.textContent = '';
      }
    }
    customElements.define('saved-items', SavedItems);
  }
})();
